<template>
  <div class="vc-support-ticket-thread">
    <button class="btn btn-sm btn-link ps-0 mb-2" @click="$emit('back')">
      <i class="bi bi-arrow-left"></i> {{ $t('messages.backToList') }}
    </button>

    <div v-if="isLoading" class="d-flex align-items-center justify-content-center" style="min-height:150px">
      <div class="spinner-border mx-2" role="status"></div>
    </div>

    <div v-else-if="loadError" class="alert alert-danger" role="alert">
      {{ loadError }}
      <div class="mt-2"><button class="btn btn-sm btn-outline-danger" @click="loadThread">{{ $t('messages.refresh') }}</button></div>
    </div>

    <div v-else-if="thread" class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 class="mb-1">{{ thread.subject }}</h5>
            <div class="text-muted small">{{ $t('messages.ticketNumber', [thread.id]) }}</div>
          </div>
          <span class="badge rounded-pill" :class="statusBadgeClass(thread.status)">{{ statusLabel(thread.status) }}</span>
        </div>

        <div v-for="comment in thread.comments" :key="comment.id" class="d-flex mb-3"
          :class="comment.authorRole === 'end-user' ? 'justify-content-end' : 'justify-content-start'">
          <div class="p-3 rounded-3" style="max-width:80%;"
            :class="comment.authorRole === 'end-user' ? 'bg-primary text-white' : 'bg-light border'">
            <div class="fw-semibold small mb-1">{{ comment.authorRole === 'end-user' ? $t('messages.you') : $t('messages.agent') }}</div>
            <div style="white-space:pre-wrap;">{{ comment.body }}</div>
            <div v-if="comment.attachments && comment.attachments.length" class="mt-2">
              <a v-for="a in comment.attachments" :key="a.url" :href="a.url" target="_blank" rel="noopener"
                class="d-block small vc-attachment-link">
                <i class="bi bi-paperclip me-1"></i>{{ a.name }}
              </a>
            </div>
            <div class="small mt-1" :class="comment.authorRole === 'end-user' ? 'vc-timestamp-on-primary' : 'text-muted'">
              {{ formatCommentTime(comment.createdAt) }}
            </div>
          </div>
        </div>

        <div v-if="isClosed" class="alert alert-light border text-center small mb-0">
          {{ $t('messages.closedNotice') }}
        </div>
        <form v-else @submit.prevent="sendReply" class="mt-3">
          <div v-if="replyError" class="alert alert-danger py-2 small">{{ replyError }}</div>
          <textarea class="form-control mb-2" rows="3" v-model="replyBody"
            :placeholder="$t('messages.replyPlaceholder')" :disabled="sending"></textarea>

          <div v-if="attachments.length" class="list-group mb-2">
            <div v-for="a in attachments" :key="a.name + a.status" class="list-group-item d-flex align-items-center justify-content-between py-2">
              <span class="small text-truncate me-2">
                <span v-if="a.status === 'uploading'" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else-if="a.status === 'error'" class="bi bi-exclamation-triangle text-danger me-1"></i>
                <i v-else class="bi bi-paperclip me-1"></i>
                {{ a.name }}
                <span v-if="a.status === 'error'" class="text-danger"> - {{ a.error }}</span>
              </span>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="a.status === 'uploading'"
                @click="removeAttachment(a)">{{ $t('messages.removeAttachment') }}</button>
            </div>
          </div>
          <input type="file" ref="attachmentInput" class="d-none" multiple @change="onAttachmentFilesSelected" />
          <button type="button" class="btn btn-outline-primary btn-sm mb-2" :disabled="sending || attachments.length >= maxAttachments"
            @click="$refs.attachmentInput.click()">
            <i class="bi bi-paperclip me-1"></i>{{ $t('messages.addAttachment') }}
          </button>
          <div>
            <button type="submit" class="btn btn-primary btn-sm" :disabled="sending || !replyBody.trim() || hasUploadingAttachments">
              <span v-if="sending" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ sending ? $t('messages.sending') : $t('messages.sendReply') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_support";
import translations from '@/translations/supportticket.js';
import attachmentsMixin from '@/mixins/supportTicketAttachmentsMixin.js';

/** Ticket thread + reply view (design doc §8.2/§8.3) - Mode A only, session-gated. */
export default {
  name: 'TicketThread',
  mixins: [attachmentsMixin],
  i18n: {
    messages: {
      en: translations.en,
      de: translations.de,
    },
  },
  props: {
    ticketId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      loadError: null,
      thread: null,
      replyBody: '',
      replyError: null,
      sending: false,
    };
  },
  computed: {
    isClosed() {
      return this.thread && (this.thread.status === 'solved' || this.thread.status === 'closed');
    },
  },
  methods: {
    formatCommentTime(iso) {
      if (!iso) return '';
      var date = new Date(iso);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    statusLabel(status) {
      var map = {
        new: 'statusNew', open: 'statusOpen', pending: 'statusPending',
        hold: 'statusHold', solved: 'statusSolved', closed: 'statusClosed',
      };
      var key = map[status];
      return key ? this.$t('messages.' + key) : (status || '');
    },
    statusBadgeClass(status) {
      if (status === 'solved' || status === 'closed') return 'bg-secondary';
      if (status === 'pending') return 'bg-warning text-dark';
      return 'bg-primary';
    },
    loadThread() {
      var me = this;
      me.isLoading = true;
      me.loadError = null;
      window.vcCommunicator.getMySupportTicket(me.ticketId)
        .then(function (result) {
          me.isLoading = false;
          me.thread = result;
        })
        .catch(function (error) {
          me.isLoading = false;
          me.loadError = '' + error;
        });
    },
    sendReply() {
      var me = this;
      if (!me.replyBody.trim()) return;
      me.sending = true;
      me.replyError = null;
      window.vcCommunicator.replyToSupportTicket(me.ticketId, me.replyBody.trim(), me.attachmentTokens)
        .then(function () {
          me.sending = false;
          me.replyBody = '';
          me.resetAttachments();
          me.loadThread();
        })
        .catch(function (error) {
          me.sending = false;
          me.replyError = '' + error;
        });
    },
  },
  mounted() {
    this.loadThread();
  },
};
</script>

<style scoped>
/* Bootstrap's own `a { color: var(--bs-link-color) }` rule overrides an inherited/utility text
   color on anchors, so an attachment link inside the blue "You" bubble rendered dark-blue-on-blue
   (effectively invisible) despite the bubble itself using .text-white. Target the exact bubble
   background so this only affects the end-user bubble, not agent replies. */
.bg-primary .vc-attachment-link,
.bg-primary .vc-attachment-link:visited {
  color: #fff !important;
}

.vc-timestamp-on-primary {
  color: rgba(255, 255, 255, 0.75);
}
</style>
